import { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, AlertTriangle, Info, Bell, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Sound effects (base64 encoded short beeps)
const SOUNDS = {
  success: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Coverage...',
  warning: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2C...',
  info: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2C...'
};

const ALERT_CONFIGS = {
  task_completed: {
    icon: CheckCircle,
    bgColor: 'from-green-500 to-emerald-600',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    defaultTitle: 'Tâche Terminée',
    sound: 'success'
  },
  room_out_of_service: {
    icon: AlertTriangle,
    bgColor: 'from-red-500 to-rose-600',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    defaultTitle: 'Chambre Hors Service',
    sound: 'warning'
  },
  maintenance_started: {
    icon: Info,
    bgColor: 'from-blue-500 to-indigo-600',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    defaultTitle: 'Maintenance Démarrée',
    sound: 'info'
  },
  new_complaint: {
    icon: Bell,
    bgColor: 'from-orange-500 to-amber-600',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    defaultTitle: 'Nouvelle Réclamation',
    sound: 'warning'
  },
  checklist_completed: {
    icon: CheckCircle,
    bgColor: 'from-purple-500 to-violet-600',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    defaultTitle: 'Checklist Complétée',
    sound: 'success'
  }
};

export function AlertPopup({ 
  isOpen, 
  onClose, 
  type = 'task_completed',
  title,
  message,
  details,
  autoCloseDelay = 5000,
  soundEnabled = true
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  
  const config = ALERT_CONFIGS[type] || ALERT_CONFIGS.task_completed;
  const Icon = config.icon;

  // Play sound
  const playSound = useCallback(() => {
    if (soundEnabled && config.sound) {
      try {
        // Use Web Audio API for a simple beep
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = type.includes('warning') || type.includes('out_of_service') ? 440 : 880;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      } catch (e) {
        console.log('Audio not available');
      }
    }
  }, [soundEnabled, config.sound, type]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setProgress(100);
      playSound();
      
      // Auto-close timer
      const startTime = Date.now();
      const timer = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / autoCloseDelay) * 100);
        setProgress(remaining);
        
        if (remaining <= 0) {
          clearInterval(timer);
          handleClose();
        }
      }, 50);
      
      return () => clearInterval(timer);
    }
  }, [isOpen, autoCloseDelay, playSound]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Popup Card */}
      <div 
        className={`relative w-full max-w-lg transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-white/20 rounded-t-2xl overflow-hidden">
          <div 
            className="h-full bg-white transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Main card */}
        <div className={`bg-gradient-to-br ${config.bgColor} rounded-2xl shadow-2xl overflow-hidden`}>
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          {/* Content */}
          <div className="p-8 text-center">
            {/* Icon */}
            <div className={`w-20 h-20 ${config.iconBg} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
              <Icon className={`w-10 h-10 ${config.iconColor}`} />
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-3">
              {title || config.defaultTitle}
            </h2>
            
            {/* Message */}
            {message && (
              <p className="text-white/90 text-lg mb-4">
                {message}
              </p>
            )}
            
            {/* Details */}
            {details && (
              <div className="bg-white/10 rounded-xl p-4 mb-6">
                <p className="text-white/80 text-sm">
                  {details}
                </p>
              </div>
            )}
            
            {/* Action button */}
            <Button
              onClick={handleClose}
              className="bg-white text-gray-800 hover:bg-gray-100 font-semibold px-8 py-3 rounded-xl shadow-lg"
            >
              OK, Compris
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to manage alerts
export function useAlertPopup() {
  const [alertState, setAlertState] = useState({
    isOpen: false,
    type: 'task_completed',
    title: '',
    message: '',
    details: '',
    soundEnabled: true
  });

  const showAlert = useCallback((options) => {
    setAlertState({
      isOpen: true,
      type: options.type || 'task_completed',
      title: options.title || '',
      message: options.message || '',
      details: options.details || '',
      soundEnabled: options.soundEnabled !== false
    });
  }, []);

  const hideAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, []);

  return {
    alertState,
    showAlert,
    hideAlert,
    AlertComponent: (
      <AlertPopup
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        details={alertState.details}
        soundEnabled={alertState.soundEnabled}
      />
    )
  };
}

export default AlertPopup;
